import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { authAPI } from '../services/api';
import { useClubSettings } from '../contexts/ClubSettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { getErrorMessage } from '../utils/errorMessage';
import { clubLogoSrc, handleClubLogoError } from '../utils/clubLogo';
import BrandLockup from '../components/ui/BrandLockup';
import { PASSWORD_HINT, PASSWORD_MIN_LENGTH, validatePassword } from '../utils/passwordPolicy';

/**
 * Új jelszó beállítása az emailben kapott linkkel. Sikeres beállítás után
 * rögtön be is léptetjük a felhasználót.
 */
const ResetPassword: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { settings } = useClubSettings();
  const { applySession } = useAuth();
  usePageTitle('Új jelszó');

  const [email, setEmail] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const preview = await authAPI.getPasswordReset(token);
      setEmail(preview.email);
    } catch (err) {
      setLoadError(getErrorMessage(err, 'A link nem érvényes.'));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('A két jelszó nem egyezik.');
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setSubmitting(true);
    try {
      const session = await authAPI.resetPassword(token, password);
      applySession(session);
      navigate('/', { replace: true });
    } catch (err) {
      setError(getErrorMessage(err, 'Az új jelszót nem sikerült beállítani.'));
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-md">
        {settings?.clubName && (
          <div className="mb-4 flex justify-center">
            <img
              src={clubLogoSrc(settings.crestUrl)}
              alt=""
              className="h-20 w-20 object-contain"
              onError={handleClubLogoError}
            />
          </div>
        )}

        {settings?.clubName ? (
          <h1 className="text-center text-3xl font-extrabold text-gray-900">{settings.clubName}</h1>
        ) : (
          <h1 className="flex justify-center">
            <BrandLockup size="lg" />
          </h1>
        )}

        {loading ? (
          <div className="mt-8 flex justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary-600" />
          </div>
        ) : loadError ? (
          <div className="mt-8 rounded-lg bg-white p-6 text-left shadow-sm">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 text-amber-500" />
              <div>
                <p className="font-medium text-gray-900">A link nem használható</p>
                <p className="mt-1 text-sm text-gray-500">{loadError}</p>
                <p className="mt-3 text-sm text-gray-500">
                  A link 1 óráig érvényes, és csak egyszer használható. Kérj újat.
                </p>
              </div>
            </div>
            <Link
              to="/forgot-password"
              className="mt-4 block w-full rounded-md bg-primary-600 px-4 py-2 text-center text-sm font-semibold text-white hover:bg-primary-700"
            >
              Új link kérése
            </Link>
            <Link
              to="/login"
              className="mt-2 block w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-center text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Vissza a bejelentkezéshez
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-5 rounded-lg bg-white p-6 text-left shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">Új jelszó beállítása</h2>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Email cím</label>
              <input
                type="email"
                value={email ?? ''}
                readOnly
                autoComplete="username"
                className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-600"
              />
            </div>

            <div>
              <label htmlFor="reset-password" className="mb-1 block text-sm font-medium text-gray-700">
                Új jelszó
              </label>
              <input
                id="reset-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={PASSWORD_MIN_LENGTH}
                autoFocus
                autoComplete="new-password"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-gray-400">{PASSWORD_HINT}</p>
            </div>

            <div>
              <label htmlFor="reset-password2" className="mb-1 block text-sm font-medium text-gray-700">
                Új jelszó megerősítése
              </label>
              <input
                id="reset-password2"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
              />
            </div>

            {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-md bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting ? 'Mentés...' : 'Jelszó mentése és belépés'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
